import moment from "moment"
import { getPresignUploadUrl } from "../config/api.js"
import { devLog } from "../config/devLog.js"

/** Comma-separated image_url from API → trimmed non-empty strings (never throws on null). */
function splitListingImageUrls(imageUrl) {
  if (imageUrl == null) return [];
  return String(imageUrl)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Display title for category 2: avoid duplicating "… Items for Sale" if the category already says so. */
function utilityListingDisplayTitle(listingCategory) {
  const raw = String(listingCategory ?? "").trim();
  if (!raw) return "Items for Sale";
  const lower = raw.toLowerCase();
  const alreadySalePhrased =
    lower.includes("items for sale") ||
    lower.includes("item for sale") ||
    lower.includes("sale items");
  if (alreadySalePhrased) return raw;
  return `${raw} Items for Sale`;
}

/** API may return utility_items as JSON string or already-parsed array (e.g. mysql2). */
export function parseUtilityItems(raw) {
  if (raw == null || raw === "") return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  const s = typeof raw === "string" ? raw.trim() : String(raw);
  if (!s) return [];
  try {
    const parsed = JSON.parse(s);
    if (Array.isArray(parsed)) return parsed.filter(Boolean);
    if (parsed && typeof parsed === "object") return [parsed];
    return [];
  } catch {
    return [];
  }
}

export function filterPosts (posts){
    if(!Array.isArray(posts)){
        return []
    }
    let res = posts.map((post)=>{
          if(post.category_id===1){
            return {
              id: post.id,
              title: post.title,
              price: post.listing_category.toLowerCase() === "sale"? "$"+post.sale_price : "$"+post.monthly_rent,
              location: post.location,
              postedBy: post.first_name+" "+post.last_name,
              amenities: post.amenities.split(","),
              createdAt: post.created_date,
              images: splitListingImageUrls(post.image_url),
              listingCategory: post.listing_category,
              category: post.category_id
            }
          }
          else if(post.category_id===2){
            const utilities = parseUtilityItems(post.utility_items)
            return {
              id: post.id,
              title: utilityListingDisplayTitle(post.listing_category),
              price:  moment(post.available_from).format("YYYY/MM/DD"),
              location: post.location,
              postedBy: post.first_name+" "+post.last_name,
              amenities: utilities.map(item => item.name),
              createdAt: post.created_date,
              images: splitListingImageUrls(post.image_url),
              listingCategory: post.listing_category,
              category: post.category_id
            }
          }
          else if(post.category_id===3){
            return {
              id: post.id,
              title: post.title,
              price: post.listing_category,
              location: moment(post.key_date).format("YYYY/MM/DD"),
              postedBy: post.first_name+" "+post.last_name,
              amenities: [post.details],
              createdAt: post.created_date,
              images: splitListingImageUrls(post.image_url),
              listingCategory: post.listing_category,
              category: post.category_id
    
            }
          }
          return null;
        }).filter(Boolean);

    return res;
}

export function getDetails(post){
  if(post.category_id===1 && post.listing_category.toLowerCase()==="sale"){
    return {
      Title: post.title,
      Category: post.listing_category,
      Price: "$"+post.sale_price,
      Advance:post.advance,
      Pincode: post.pincode,
      Bedrooms:post.bedrooms,
      Bathrooms:post.bathrooms,
      Size:post.square_feet,
      Deposit:post.deposit,
      "Available From": moment(post.available_from).format("YYYY/MM/DD"),
      "Pets Allowed": post.pets_allowed===1?"Yes":"No",
      "Smoking Allowed": post.smoking_allowed===1?"Yes":"No",
      "Parking Available":post.is_parking_available===1?"Yes":"No",
      "Nearby Groceries":post.nearby_groceries,
      "Nearby Schools": post.nearby_schools,
      "Bus Connectivity":post.bus_connectivity,
      "Additional Details":post.additional_detail
    }
  }
  else if(post.category_id===1 && post.listing_category.toLowerCase()==="rent"){
    return {
      Title: post.title,
      Category: post.listing_category,
      Price: "$"+post.monthly_rent+"/monthly",
      Pincode: post.pincode,
      Bedrooms:post.bedrooms,
      Bathrooms:post.bathrooms,
      Size:post.square_feet,
      Deposit:post.deposit,
      "Rental Duration": post.rental_duration,
      "Rental Type": post.rental_type,
      "Available From": moment(post.available_from).format("YYYY/MM/DD"),
      "Food Preference": post.food_preference,
      "Preferred Gender": post.preferred_gender,
      "Pets Allowed": post.pets_allowed===1?"Yes":"No",
      "Smoking Allowed": post.smoking_allowed===1?"Yes":"No",
      "Parking Available":post.is_parking_available===1?"Yes":"No",
      "Nearby Groceries":post.nearby_groceries,
      "Bus Connectivity":post.bus_connectivity,
      "Additional Details":post.additional_detail
    }
  }
  else if(post.category_id===2){
    return {
      Title: utilityListingDisplayTitle(post.listing_category),
      Category: post.listing_category,
      Location: post.location,
      "Available From": moment(post.available_from).format("YYYY/MM/DD"),
      "Available To": moment(post.available_to).format("YYYY/MM/DD"),
      "Additional Details": post.additional_detail,
      Utilities: parseUtilityItems(post.utility_items),
    }
  }
  else if(post.category_id===3){
    return {
      Title: post.title,
      Category: post.listing_category,
      "Key Date": moment(post.key_date).format("YYYY/MM/DD"),
      Details: post.details,
      "Additional Details": post.additional_information
    }

  }
}

export const formatTimestamp = (timestamp) => {

    const now = moment();
    const time = moment(timestamp);
  
    const diffMinutes = now.diff(time, "minutes");
    const diffHours = now.diff(time, "hours");
  
    if (diffMinutes < 60) {
      return `${diffMinutes} minutes ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hours ago`;
    } else {
      return time.format("YYYY-MM-DD"); // Show date if more than a day old
    }
  };


/**
 * Upload files to S3 via a presigned URL from your API (no AWS keys in the browser).
 *
 * Backend contract — POST getPresignedUploadUrl (JSON):
 *   Request:  { fileName: string, contentType: string }
 *   Response: { uploadUrl: string, publicUrl: string }
 *   Optional: { headers?: Record<string,string> } — extra headers the browser must send with PUT (e.g. x-amz-acl if signed).
 *
 * Alternative response (public URL derived on the client):
 *   { uploadUrl, key, bucket, region }
 *
 * Env: REACT_APP_API_BASE, or REACT_APP_PRESIGN_UPLOAD_URL for the full presign endpoint URL.
 */
export const uploadToS3 = async (files) => {
  const collectFiles = (input) => {
    if (!input) return [];

    try {
      if (typeof File !== "undefined" && input instanceof File) return [input];
    } catch (e) {
      // ignore
    }

    if (
      input &&
      typeof input === "object" &&
      input.name &&
      (input.size !== undefined || input.type !== undefined)
    ) {
      return [input];
    }

    if (Array.isArray(input)) {
      return input.flatMap(collectFiles);
    }

    if (input && typeof input === "object" && typeof input.length === "number") {
      try {
        return Array.from(input).flatMap(collectFiles);
      } catch (e) {
        return [];
      }
    }

    return [];
  };

  const requestPresigned = async (fileName, contentType) => {
    const token =
      typeof localStorage !== "undefined" ? localStorage.getItem("token") : null;
    const headers = { "Content-Type": "application/json" };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const res = await fetch(getPresignUploadUrl(), {
      method: "POST",
      headers,
      body: JSON.stringify({ fileName, contentType }),
    });

    const raw = await res.text();
    let data;
    try {
      data = raw ? JSON.parse(raw) : {};
    } catch {
      throw new Error(`Presign response is not JSON: ${raw.slice(0, 200)}`);
    }

    if (!res.ok) {
      throw new Error(data.message || data.error || raw || `Presign failed (${res.status})`);
    }

    const uploadUrl = data.uploadUrl || data.url;
    if (!uploadUrl) {
      throw new Error("Presign response missing uploadUrl");
    }

    let publicUrl = data.publicUrl;
    if (!publicUrl && data.key && data.bucket && data.region) {
      const encodedKey = data.key.split("/").map(encodeURIComponent).join("/");
      publicUrl = `https://${data.bucket}.s3.${data.region}.amazonaws.com/${encodedKey}`;
    }
    if (!publicUrl) {
      throw new Error("Presign response missing publicUrl (or key/bucket/region)");
    }

    return { uploadUrl, publicUrl, extraHeaders: data.headers || {} };
  };

  const putToPresignedUrl = async (fileObj) => {
    const fileName = fileObj && fileObj.name ? fileObj.name : `file_${Date.now()}.jpg`;
    const contentType = (fileObj && fileObj.type) || "image/jpeg";
    const { uploadUrl, publicUrl, extraHeaders } = await requestPresigned(
      fileName,
      contentType
    );

    const putHeaders = {
      "Content-Type": contentType,
      ...extraHeaders,
    };

    const putRes = await fetch(uploadUrl, {
      method: "PUT",
      body: fileObj,
      headers: putHeaders,
    });

    if (!putRes.ok) {
      const errText = await putRes.text().catch(() => "");
      throw new Error(errText || `S3 upload failed (${putRes.status})`);
    }

    return publicUrl;
  };

  const fileObjects = collectFiles(files);
  if (!fileObjects || fileObjects.length === 0) return [];

  try {
    return await Promise.all(fileObjects.map((f) => putToPresignedUrl(f)));
  } catch (err) {
    devLog("Upload error:", err?.message);
    throw err;
  }
};